import React, { useState, useEffect } from 'react';
import { Mail, Phone, MessageSquare, Heart, Calendar, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminNotifications = () => {
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/notifications`);
      const data = await response.json();
      setNotifications(data);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && !notifications) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <p>Loading notifications...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <h1 className="hero-medium">Ministry Notifications</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
            Last updated: {lastUpdated}
          </span>
          <button 
            onClick={fetchNotifications}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Contact Submissions */}
      <section style={{ marginBottom: '48px' }}>
        <h2 className="heading-2" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Mail size={24} style={{ color: 'var(--text-primary)' }} />
          Recent Contact Messages ({notifications?.contact_submissions?.length || 0})
        </h2>
        
        {notifications?.contact_submissions?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {notifications.contact_submissions.map((contact) => (
              <div key={contact.id} style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 className="heading-3">{contact.name}</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {contact.email} • {formatDate(contact.created_at)}
                    </p>
                  </div>
                  <span className={`body-small ${contact.status === 'new' ? 'font-weight: 600; color: var(--status-error)' : ''}`}>
                    {contact.status === 'new' ? '🔴 NEW' : '✅ Reviewed'}
                  </span>
                </div>
                
                {contact.subject && (
                  <p className="body-regular" style={{ marginBottom: '12px', fontWeight: '600' }}>
                    Subject: {contact.subject}
                  </p>
                )}
                
                <p className="body-regular" style={{ 
                  color: 'var(--text-secondary)', 
                  background: 'var(--bg-primary)', 
                  padding: '16px', 
                  borderRadius: '0px',
                  borderLeft: '4px solid var(--text-primary)'
                }}>
                  {contact.message}
                </p>
                
                <div style={{ marginTop: '16px' }}>
                  <a 
                    href={`mailto:${contact.email}?subject=Re: ${contact.subject || 'Your message to Khesed+ Avad'}`}
                    className="btn-primary"
                    style={{ textDecoration: 'none' }}
                  >
                    Reply to {contact.name}
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>No recent contact messages</p>
        )}
      </section>

      {/* Prayer Requests */}
      <section style={{ marginBottom: '48px' }}>
        <h2 className="heading-2" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Heart size={24} style={{ color: 'var(--text-primary)' }} />
          Prayer Requests ({notifications?.prayer_requests?.length || 0})
        </h2>
        
        {notifications?.prayer_requests?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {notifications.prayer_requests.map((prayer) => (
              <div key={prayer.id} style={{
                background: prayer.urgency === 'urgent' ? '#fff5f5' : 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: prayer.urgency === 'urgent' ? '2px solid var(--status-error)' : '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 className="heading-3">{prayer.name || 'Anonymous Request'}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {prayer.email && (
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          📧 {prayer.email}
                        </p>
                      )}
                      {prayer.phone && (
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          📞 {prayer.phone}
                        </p>
                      )}
                      <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(prayer.created_at)}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {prayer.urgency === 'urgent' && (
                      <span style={{ 
                        background: 'var(--status-error)', 
                        color: 'white', 
                        padding: '4px 8px', 
                        borderRadius: '0px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        URGENT
                      </span>
                    )}
                    <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>
                      Status: {prayer.status === 'new' ? '🔴 Needs Call' : 
                               prayer.status === 'called' ? '✅ Called' : '🙏 Prayed For'}
                    </p>
                  </div>
                </div>
                
                <p className="body-regular" style={{ 
                  color: 'var(--text-primary)', 
                  background: 'var(--bg-primary)', 
                  padding: '16px', 
                  borderRadius: '0px',
                  borderLeft: '4px solid var(--text-primary)',
                  marginBottom: '16px'
                }}>
                  <strong>Prayer Request:</strong><br />
                  {prayer.request}
                </p>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  {prayer.phone && (
                    <a 
                      href={`tel:${prayer.phone}`}
                      className="btn-primary"
                      style={{ textDecoration: 'none' }}
                    >
                      📞 Call to Pray
                    </a>
                  )}
                  {prayer.email && (
                    <a 
                      href={`mailto:${prayer.email}?subject=Prayer Follow-up from Khesed+ Avad`}
                      className="btn-secondary"
                      style={{ textDecoration: 'none' }}
                    >
                      📧 Send Email
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>No recent prayer requests</p>
        )}
      </section>

      {/* Service Inquiries */}
      <section>
        <h2 className="heading-2" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MessageSquare size={24} style={{ color: 'var(--text-primary)' }} />
          Service Inquiries ({notifications?.service_inquiries?.length || 0})
        </h2>
        
        {notifications?.service_inquiries?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {notifications.service_inquiries.map((inquiry) => (
              <div key={inquiry.id} style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h3 className="heading-3">{inquiry.name}</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {inquiry.email} • {inquiry.phone} • {formatDate(inquiry.created_at)}
                    </p>
                  </div>
                  <span className="body-small" style={{ 
                    background: inquiry.service_type.includes('Coaching') ? 'var(--status-success)' : 'var(--bg-primary)',
                    color: inquiry.service_type.includes('Coaching') ? 'white' : 'var(--text-primary)',
                    padding: '4px 8px',
                    borderRadius: '0px',
                    fontSize: '12px'
                  }}>
                    {inquiry.service_type.includes('Coaching') ? 'FREE SESSION' : inquiry.service_type}
                  </span>
                </div>
                
                <p className="body-regular" style={{ 
                  color: 'var(--text-primary)', 
                  marginBottom: '16px',
                  fontWeight: '600'
                }}>
                  Interested in: {inquiry.service_type}
                </p>
                
                {inquiry.message && (
                  <p className="body-regular" style={{ 
                    color: 'var(--text-secondary)', 
                    background: 'var(--bg-primary)', 
                    padding: '16px', 
                    borderRadius: '0px',
                    marginBottom: '16px'
                  }}>
                    {inquiry.message}
                  </p>
                )}
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <a 
                    href={`mailto:${inquiry.email}?subject=Re: Your Interest in ${inquiry.service_type}`}
                    className="btn-primary"
                    style={{ textDecoration: 'none' }}
                  >
                    📧 Respond
                  </a>
                  {inquiry.phone && (
                    <a 
                      href={`tel:${inquiry.phone}`}
                      className="btn-secondary"
                      style={{ textDecoration: 'none' }}
                    >
                      📞 Call
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>No recent service inquiries</p>
        )}
      </section>
    </div>
  );
};